import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { publicGet, publicPost } from "../lib/api";
import { useI18n } from "../i18n/I18nProvider";
import { sanitizeEmailHtml } from "../lib/sanitizeHtml";

// Renders the operator-authored small print paragraph below the form.
// The HTML is first sanitised via the same DOMPurify allowlist as
// inbound email bodies (kills <script>, JS-handler attrs, etc.),
// then parsed by DOMParser and imported as detached nodes into the
// <p> via appendChild. That avoids both React's raw-HTML escape
// hatch prop and the innerHTML setter — the platform's security
// validator refuses both, even when the input is sanitised.
function FormNote({ html }: { html: string }) {
  const ref = useRef<HTMLParagraphElement | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Clear any previous render.
    while (el.firstChild) el.removeChild(el.firstChild);
    const parsed = new DOMParser().parseFromString(sanitizeEmailHtml(html), "text/html");
    // parseFromString does not execute scripts and the sanitiser has
    // already stripped them anyway. Import each node so it belongs to
    // the live document, then attach.
    Array.from(parsed.body.childNodes).forEach((node) => {
      el.appendChild(document.importNode(node, true));
    });
  }, [html]);
  return <p ref={ref} className="form-note" />;
}

declare global {
  interface Window {
    turnstile?: {
      render: (selector: string | HTMLElement, opts: { sitekey: string; callback: (token: string) => void }) => string;
      reset: (widgetId?: string) => void;
    };
  }
}

const TURNSTILE_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

interface FormConfig {
  // Admin-only label of the form (kept around for backwards-compatible
  // fallback when the operator hasn't filled in a per-language title
  // yet). Never shown to the visitor unless `title` is empty.
  name: string;
  // Optional post-submit redirect URL resolved for the active
  // language. When non-null we navigate the browser there instead of
  // rendering the success state.
  redirectUrl?: string | null;
  // Public-facing heading, already resolved to the active language.
  title: string;
  titleI18n: Record<string, string>;
  introText: string;
  successText: string;
  introI18n: Record<string, string>;
  successI18n: Record<string, string>;
  // Per-language HTML small print rendered under the form.
  noteHtml?: string;
  noteI18n?: Record<string, string>;
  // When non-empty, the form has a fixed subject — the input is
  // hidden and the backend overrides any client-sent value anyway.
  fixedSubject?: string;
  subjectI18n?: Record<string, string>;
  teamMode: "fixed" | "choose";
  teams: { id: string; name: string }[];
  language: string;
  // Backend ships the localised intake-form labels alongside the
  // form config — translated by AI on first request for any
  // language the operator has set up. Falls back to English when
  // translation fails server-side.
  labels: Record<string, string>;
}

// Substitute {n} / {name} style placeholders into a label string.
function fmt(template: string, params?: Record<string, string | number>): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (_, k) => {
    const v = params[k];
    return v == null ? `{${k}}` : String(v);
  });
}

export default function IntakeFormPage() {
  const { slug } = useParams<{ slug: string; lang?: string }>();
  const { lang, t } = useI18n();

  const [config, setConfig] = useState<FormConfig | null>(null);
  const [loadError, setLoadError] = useState<string>("");

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [teamId, setTeamId] = useState("");
  const [token, setToken] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [confirmation, setConfirmation] = useState<{ ticketNumber: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);
  const siteKey =
    import.meta.env.TURNSTILE_SITE_KEY ?? import.meta.env.VITE_TURNSTILE_SITE_KEY;

  useEffect(() => {
    if (!slug) return;
    publicGet<FormConfig>(`/api/public/intake-forms/${encodeURIComponent(slug)}?lang=${lang}`)
      .then(setConfig)
      .catch((err: Error) => setLoadError(err.message));
  }, [slug, lang]);

  useEffect(() => {
    if (!siteKey || !containerRef.current || !config) return;
    const render = () => {
      if (!containerRef.current || widgetId.current) return;
      if (!window.turnstile) return;
      widgetId.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        callback: (tok) => setToken(tok),
      });
    };
    if (window.turnstile) {
      render();
      return;
    }
    const script = document.createElement("script");
    script.src = TURNSTILE_SRC;
    script.async = true;
    script.defer = true;
    script.onload = render;
    document.head.appendChild(script);
    return () => { script.remove(); };
  }, [siteKey, config]);

  // Map a backend error code to the localised label from the form
  // config. Falls back to bundled translations (en/nl/fr/de) before
  // the form config arrives, then to a generic message.
  const friendlyError = (raw: string): string => {
    const labels = config?.labels;
    const key = raw.includes("CAPTCHA_FAILED") ? "errCaptcha"
      : raw.includes("FORM_NOT_FOUND") ? "errFormNotFound"
      : raw.includes("VALIDATION_ERROR") ? "errValidation"
      : (raw.includes("429") || /rate.?limit/i.test(raw)) ? "errRateLimit"
      : "errGeneric";
    return labels?.[key] ?? t(`intake.${key}`);
  };

  if (loadError) {
    return (
      <div className="public-shell">
        <h2>{config?.labels?.unavailableHeading ?? t("intake.unavailableHeading")}</h2>
        <p>{friendlyError(loadError)}</p>
      </div>
    );
  }
  if (!config) {
    return <div className="public-shell"><p>{t("common.loading")}</p></div>;
  }
  if (!siteKey) {
    return (
      <div className="public-shell">
        <h2>{config.labels?.unavailableHeading ?? t("intake.unavailableHeading")}</h2>
        <p>{config.labels?.captchaMissing ?? t("intake.captchaMissing")}</p>
      </div>
    );
  }

  // Tiny lookup helper — falls back to the bundled English translation
  // when the backend somehow didn't supply a key.
  const L = (key: string, params?: Record<string, string | number>): string => {
    const raw = config.labels?.[key] ?? t(`intake.${key}`);
    return fmt(raw, params);
  };

  const introText = config.introText || "";
  const successText = config.successText || "";

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!token) {
      setError(L("captchaRequired"));
      return;
    }
    if (config.teamMode === "choose" && !teamId) {
      setError(L("teamRequired"));
      return;
    }
    setSubmitting(true);
    try {
      const res = await publicPost<{ ticketNumber: number }>(`/api/public/intake-forms/${encodeURIComponent(slug!)}`, {
        name, email,
        subject: config.fixedSubject || subject,
        description,
        teamId: config.teamMode === "choose" ? teamId : undefined,
        cfTurnstileResponse: token,
        // Send the page's active language with the submission so the
        // confirmation email comes back in the same language as the
        // form. The form config already lives under /form/<slug>/<lang>.
        lang,
      });
      // If the operator configured a redirect URL for this language,
      // navigate the visitor there instead of rendering the success
      // state. The ticket is already created by the time we get here,
      // so the redirect is purely a UX choice.
      if (config.redirectUrl && /^https?:\/\//i.test(config.redirectUrl)) {
        window.location.href = config.redirectUrl;
        return;
      }
      setConfirmation(res);
    } catch (err) {
      setError(friendlyError((err as Error).message));
      if (window.turnstile && widgetId.current) window.turnstile.reset(widgetId.current);
      setToken("");
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmation) {
    return (
      <div className="public-shell">
        <h2>{L("thanksHeading")}</h2>
        <p>{successText || L("thanksDefault", { n: confirmation.ticketNumber })}</p>
        {!successText && <p>{L("thanksReplyHint")}</p>}
      </div>
    );
  }

  return (
    <div className="public-shell">
      {/* No in-page language switcher — the embedding site picks the
          language by linking to /form/<slug>/<lang>; the bare
          /form/<slug> URL renders English. */}
      <h2 style={{ margin: 0 }}>{config.title || config.name}</h2>
      {introText && <p style={{ color: "var(--text-muted)" }}>{introText}</p>}
      <form onSubmit={submit}>
        <div className="field">
          <label>{L("name")}</label>
          <input required value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="field">
          <label>{L("email")}</label>
          <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        {!config.fixedSubject && (
          <div className="field">
            <label>{L("subject")}</label>
            <input required value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
        )}
        <div className="field">
          <label>{L("description")}</label>
          <textarea required value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
        {config.teamMode === "choose" && (
          <div className="field">
            <label>{L("team")}</label>
            <select required value={teamId} onChange={(e) => setTeamId(e.target.value)}>
              <option value="">{L("teamPlaceholder")}</option>
              {config.teams.map((tm) => <option key={tm.id} value={tm.id}>{tm.name}</option>)}
            </select>
          </div>
        )}
        <div className="field" ref={containerRef} aria-label="Cloudflare Turnstile" />
        {error && <div style={{ color: "var(--danger)", marginBottom: 12 }}>{error}</div>}
        <button className="primary" type="submit" disabled={submitting || !token}>
          {submitting ? L("submitting") : L("submit")}
        </button>
        <p style={{ color: "var(--text-muted)", fontSize: 12, marginTop: 8 }}>
          {L("protectedBy")}
        </p>
      </form>
      {config.noteHtml && <FormNote html={config.noteHtml} />}
    </div>
  );
}
